// shell/components/table/columns/labeled-text-column.ts

import { LabeledTextColumnType } from '../types';
import { TextColumnRenderer } from './text-column';
import { CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class LabeledTextColumnRenderer<T = unknown> extends TextColumnRenderer<T> {
    constructor(private labelConfig: LabeledTextColumnType<T>) {
        super({ kind: 'text', converter: labelConfig.converter });
    }

    getGridCellClass(): string {
        return 'cs-column-labeled';
    }

    getTableCellClass(): string {
        return 'cs-column-labeled';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'cs-column-labeled__wrapper';

        // Label
        const label = document.createElement('span');
        label.className = 'cs-column-labeled__label';
        label.textContent = this.labelConfig.label ?? ctx.column.header;
        wrapper.appendChild(label);

        // Value (with potential shading)
        const valueWrapper = document.createElement('span');
        valueWrapper.className = 'cs-column-labeled__value';

        const valueEl = this.buildValueElement(ctx);
        valueWrapper.appendChild(valueEl);
        wrapper.appendChild(valueWrapper);

        // Cache elements for efficient updates
        this.cacheElement(cellWrapper, 'wrapper', wrapper);
        this.cacheElement(cellWrapper, 'valueWrapper', valueWrapper);
        this.cacheElement(cellWrapper, 'valueEl', valueEl);

        // Apply shading to value wrapper (consistent with update)
        if (this.shouldShadeInGrid(ctx)) {
            this.applyShade(valueWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }

        return wrapper;
    }

    /**
     * Build just the value element (separate from wrapper).
     */
    protected buildValueElement(ctx: CellRenderContext<T>): HTMLSpanElement {
        const span = document.createElement('span');
        span.className = 'cs-column-labeled__value-text';
        span.textContent = this.formatValue(ctx.value, this.labelConfig.converter, ctx.item);
        return span;
    }

    buildTableCell(ctx: CellRenderContext<T>, td: HTMLElement): HTMLElement {
        // In table mode, just show value (label is the header)
        return super.buildTableCell(ctx, td);
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        const valueWrapper = this.getCachedElement(cellWrapper, 'valueWrapper');

        if (type === CellUpdateType.Shades) {
            if (valueWrapper && this.shouldShadeInGrid(ctx)) {
                this.applyShade(valueWrapper, ctx.shadeRank, ctx.heatMapBounds);
            }
            return { contentElement: contentEl };
        }

        // Full update
        const valueEl = this.getCachedElement(cellWrapper, 'valueEl');
        if (valueEl) {
            const newValueEl = this.buildValueElement(ctx);
            valueEl.replaceWith(newValueEl);
            this.cacheElement(cellWrapper, 'valueEl', newValueEl);
        }

        if (valueWrapper && this.shouldShadeInGrid(ctx)) {
            this.applyShade(valueWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }

        return { contentElement: contentEl };
    }
}
